import {
    differenceInDays,
    differenceInCalendarDays,
    parseISO,
    isValid,
    startOfDay,
    startOfMonth,
    endOfDay,
    endOfMonth,
    isAfter,
    isBefore,
    isEqual,
    addDays,
    addMonths,
    addYears,
    add as addFNS,
    format,
    getDaysInMonth as getDaysInMonthFNS,
    getISODay,
    differenceInMonths,
    differenceInHours,
    isToday as isTodayFNS,
    isSameDay as isSameDayFNS,
    getWeek,
    roundToNearestHours,
} from 'date-fns';

/**
 * Composable for handling dates in Vue components
 *
 * @returns {Object} - Date utility functions
 */
export const useDate = () => {
    const locale = window.__locale__ || 'en';

    const parse = (dateString) => {
        if (!dateString) return null;

        if (dateString instanceof Date) {
            return isValid(dateString) ? dateString : null;
        }

        const parsed = parseISO(dateString);
        return isValid(parsed) ? parsed : null;
    };

    // Returns
    const getHours = (date) => {
        const dateObj = typeof date === 'string' ? parse(date) : date;
        if (!dateObj) return null;
        return dateObj.getHours();
    };

    const getDaysInMonth = (date) => {
        const dateObj = typeof date === 'string' ? parse(date) : date;
        if (!dateObj) return null;
        return getDaysInMonthFNS(dateObj);
    };

    const getWeekNumber = (date) => {
        const dateObj = typeof date === 'string' ? parse(date) : date;
        if (!dateObj) return null;
        return getWeek(dateObj, { weekStartsOn: 1 }); // Start week on Monday
    };

    // Differences
    const hoursDiff = (date1, date2) => {
        const d1 = typeof date1 === 'string' ? parse(date1) : date1;
        const d2 = typeof date2 === 'string' ? parse(date2) : date2;

        return d1 && d2 ? differenceInHours(d1, d2) : 0;
    };

    // Pure hours difference calculated later hour (rounded ceiling) and earlier hour (rounded floor)
    // This is useful for calculating the difference in hours without considering minutes
    const pureHoursDiff = (date1, date2) => {
        const d1 = typeof date1 === 'string' ? parse(date1) : date1;
        const d2 = typeof date2 === 'string' ? parse(date2) : date2;
        if (!d1 || !d2) return 0;

        const hours1 = roundToNearestHours(d1, { roundingMethod: 'ceil' });
        const hours2 = roundToNearestHours(d2, { roundingMethod: 'floor' });
        return differenceInHours(hours1, hours2);
    };

    const daysDiff = (date1, date2) => {
        const d1 = typeof date1 === 'string' ? parse(date1) : date1;
        const d2 = typeof date2 === 'string' ? parse(date2) : date2;

        return d1 && d2 ? differenceInDays(d1, d2) : 0;
    };

    const calendarDaysDiff = (date1, date2) => {
        const d1 = typeof date1 === 'string' ? parse(date1) : date1;
        const d2 = typeof date2 === 'string' ? parse(date2) : date2;

        return d1 && d2 ? differenceInCalendarDays(d1, d2) : 0;
    };

    const monthsDiff = (date1, date2) => {
        const d1 = typeof date1 === 'string' ? parse(date1) : date1;
        const d2 = typeof date2 === 'string' ? parse(date2) : date2;

        return d1 && d2 ? differenceInMonths(d1, d2) : 0;
    };

    // Boundaries
    const startOf = (date, unit = 'day') => {
        const dateObj = typeof date === 'string' ? parse(date) : date;
        if (!dateObj) return null;

        switch (unit) {
            case 'day':
                return startOfDay(dateObj);
            case 'month':
                return startOfMonth(dateObj);
            default:
                return dateObj;
        }
    };

    const endOf = (date, unit = 'day') => {
        const dateObj = typeof date === 'string' ? parse(date) : date;
        if (!dateObj) return null;

        switch (unit) {
            case 'day':
                return endOfDay(dateObj);
            case 'month':
                return endOfMonth(dateObj);
            default:
                return dateObj;
        }
    };

    const daysBetweenDateTimes = (startDateTime, endDateTime) => {
        const start = startOf(startDateTime, 'day');
        const end = startOf(endDateTime, 'day');

        return daysDiff(end, start);
    };

    // Format with localization

    // Format a local date string to a human-readable format
    // Example (en): '2023-10-01T14:13' ->  'October 1, 2023'
    // Example (da): '2023-10-01T14:13' ->  '1. oktober 2023'
    const longDate = (date) => {
        const dateObj = typeof date === 'string' ? parse(date) : date;
        if (!dateObj) return null;
        return new Intl.DateTimeFormat(locale, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        }).format(new Date(dateObj));
    };

    // Format a long date with time format
    // example (en): '2023-10-01T14:13' -> 'October 1, 2023, 14:13'
    // example (da): '2023-10-01T14:13' -> '1. oktober 2023, 14.13'
    const longDateWithTime = (date) => {
        const dateObj = typeof date === 'string' ? parse(date) : date;
        if (!dateObj) return null;
        const dateOptions = { year: 'numeric', month: 'long', day: 'numeric' };
        const timeOptions = { hour: '2-digit', minute: '2-digit', hour12: false };

        let localDate = new Intl.DateTimeFormat(locale, dateOptions).format(new Date(dateObj));
        let localTime = new Intl.DateTimeFormat(locale, timeOptions).format(new Date(dateObj));
        return localDate + ', ' + localTime;
    };

    // Weekday with shortdate format
    // example (en): '2023-10-01T14:13' -> 'Sunday, 01/10/23'
    // example (da): '2023-10-01T14:13' -> 'Søndag, 01/10/23'
    const weekdayShortDate = (date) => {
        const dateObj = typeof date === 'string' ? parse(date) : date;
        if (!dateObj) return null;
        const weekDay = new Intl.DateTimeFormat(locale, { weekday: 'long' }).format(new Date(dateObj));
        return `${weekDay}, ${format(dateObj, 'dd/MM/yy')}`;
    };

    // Format month and year
    // example (en): '2023-10-01T14:13' -> 'October 2023'
    // example (da): '2023-10-01T14:13' -> 'oktober 2023'
    const monthYear = (date) => {
        const dateObj = typeof date === 'string' ? parse(date) : date;
        if (!dateObj) return null;
        return new Intl.DateTimeFormat(locale, {
            month: 'long',
            year: 'numeric',
        }).format(new Date(dateObj));
    };

    // Format month and year
    // example (en): '2023-10-01T14:13' -> 'Sun 01'
    // example (da): '2023-10-01T14:13' -> 'søn 01'
    const weekdayDay = (date) => {
        const dateObj = typeof date === 'string' ? parse(date) : date;
        if (!dateObj) return null;
        const weekday = new Intl.DateTimeFormat(locale, {
            weekday: 'short',
        })
            .format(new Date(dateObj))
            .slice(0, 3); // Get the first 3 letters of the weekday
        const day = parseDate(dateObj, 'dd');
        return `${weekday} ${day}`;
    };

    // Format relative time to now
    // This function formats a date string to a relative time string (e.g., "2 days ago")
    // Example (da): for 7 sekunder siden
    // Example (en): 7 seconds ago
    const formatRelativeTime = (date) => {
        const dateObj = typeof date === 'string' ? parse(date) : date;
        if (!dateObj) return null;
        const parsedDate = new Date(dateObj);
        const now = new Date();
        const diffInSeconds = Math.floor((now - parsedDate) / 1000);
        const absDiff = Math.abs(diffInSeconds);
        const isPast = diffInSeconds > 0;

        const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
        const sign = isPast ? -1 : 1;

        if (absDiff < 60) {
            return rtf.format(sign * Math.floor(absDiff), 'second');
        }
        if (absDiff < 3600) {
            return rtf.format(sign * Math.floor(absDiff / 60), 'minute');
        }
        if (absDiff < 86400) {
            return rtf.format(sign * Math.floor(absDiff / 3600), 'hour');
        }
        if (absDiff < 2592000) {
            return rtf.format(sign * Math.floor(absDiff / 86400), 'day');
        }
        if (absDiff < 31536000) {
            return rtf.format(sign * Math.floor(absDiff / 2592000), 'month');
        }
        return rtf.format(sign * Math.floor(absDiff / 31536000), 'year');
    };

    const parseDate = (date, formatString) => {
        const formattedDate = typeof date === 'string' ? parse(date) : date;

        return format(formattedDate, formatString);
    };

    // Manipulation

    // Add or subtract days, months, or years from a date (hours, minutes, seconds are not supported)
    const add = (date, amount, unit = 'days') => {
        const dateObj = typeof date === 'string' ? parse(date) : date;
        if (!dateObj) return null;

        switch (unit) {
            case 'days':
                return addDays(dateObj, amount);
            case 'months':
                return addMonths(dateObj, amount);
            case 'years':
                return addYears(dateObj, amount);
            default:
                return dateObj;
        }
    };

    // Subtract days, months, or years from a date (hours, minutes, seconds are not supported)
    const subtract = (date, amount, unit = 'days') => {
        return add(date, -amount, unit);
    };

    // Add or subtract any part of the date by passing an object with the parts to add or subtract
    // Example: addOrSubtract(new Date(), { days: 5, months: 2, years: 1 });
    // For subtraction, use negative values: addOrSubtract(new Date(), { days: -5, months: -2, years: -1 });
    const addOrSubtract = (date, addObj) => {
        const dateObj = typeof date === 'string' ? parse(date) : date;
        if (!dateObj) return null;

        return addFNS(dateObj, addObj);
    };

    // Comparisons
    const isAfterDate = (date1, date2) => {
        const d1 = typeof date1 === 'string' ? parse(date1) : date1;
        const d2 = typeof date2 === 'string' ? parse(date2) : date2;
        return d1 && d2 ? isAfter(d1, d2) : false;
    };

    const isBeforeDate = (date1, date2) => {
        const d1 = typeof date1 === 'string' ? parse(date1) : date1;
        const d2 = typeof date2 === 'string' ? parse(date2) : date2;
        return d1 && d2 ? isBefore(d1, d2) : false;
    };

    const isEqualDate = (date1, date2) => {
        const d1 = typeof date1 === 'string' ? parse(date1) : date1;
        const d2 = typeof date2 === 'string' ? parse(date2) : date2;
        return d1 && d2 ? isEqual(d1, d2) : false;
    };

    const isSameDay = (date1, date2) => {
        const d1 = typeof date1 === 'string' ? parse(date1) : date1;
        const d2 = typeof date2 === 'string' ? parse(date2) : date2;
        return d1 && d2 ? isSameDayFNS(d1, d2) : false;
    };

    const daysInMonth = (date) => {
        const dateObj = typeof date === 'string' ? parse(date) : date;
        if (!dateObj) return null;
        return getDaysInMonth(dateObj);
    };

    const isoWeekday = (date) => {
        const dateObj = typeof date === 'string' ? parse(date) : date;
        if (!dateObj) return null;
        return getISODay(dateObj); // Returns 1-7 (Monday-Sunday)
    };

    const isToday = (date) => {
        const dateObj = typeof date === 'string' ? parse(date) : date;
        if (!dateObj) return false;
        return isTodayFNS(dateObj);
    };

    return {
        startOf,
        endOf,
        parse,
        daysDiff,
        monthsDiff,
        daysBetweenDateTimes,
        longDateWithTime,
        parseDate,
        isAfterDate,
        isBeforeDate,
        isEqualDate,
        isSameDay,
        add,
        addOrSubtract,
        subtract,
        daysInMonth,
        isoWeekday,
        getHours,
        calendarDaysDiff,
        hoursDiff,
        getDaysInMonth,
        isToday,
        weekdayShortDate,
        longDate,
        formatRelativeTime,
        monthYear,
        weekdayDay,
        getWeekNumber,
        pureHoursDiff,
    };
};
